import { memo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import numbro from 'numbro';

import { WebPlanDetails, WebPlanTier } from '../../../constants/allowance';
import { SVGIconType } from '../../../constants/svgIcon';
import { SubscriptionType } from '../../../models/SpaceStore';
import { LightSwipe } from '../LightSwipe';
import { PngIcon } from '../PngIcon';
import { ProgressBar } from '../ProgressBar';
import { SvgIcon } from '../SVGIcon';

import styles from './SubscriptionCard.module.css';

export enum SubscriptionCardOverlayTag {
  Current = 'Current Plan',
  CurrentMonthly = 'Current Plan: Monthly',
  CurrentYearly = 'Current Plan: Yearly',
  Scheduled = 'Scheduled Plan',
}

export type SubscriptionCardProps = {
  tier: WebPlanTier | undefined;
  period: SubscriptionType.Monthly | SubscriptionType.Yearly | undefined;
  expiredAt?: Date;
  willAutoRenew?: boolean;
  currency?: 'USD' | 'HKD' | 'CNY' | 'TWD';
  createdItemCount?: number;
  overlayTag?: SubscriptionCardOverlayTag;
  onClickSubscription?: () => void;
  onClickRegister?: () => void;
  onClickUpgrade?: () => void;
  onClickDetail?: () => void;
};

export const SubscriptionCard = memo(
  ({
    tier,
    period,
    expiredAt,
    willAutoRenew,
    currency,
    createdItemCount,
    overlayTag,
    onClickSubscription: onClickSubscribe,
    onClickRegister,
    onClickUpgrade,
    onClickDetail,
  }: SubscriptionCardProps) => {
    const { t, i18n } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);

    const isTrial = onClickRegister !== undefined;

    const {
      name,
      storage,
      token,
      creationToken,
      unlockOcr,
      monthlyPrice,
      yearlyPrice,
    } = WebPlanDetails[tier ?? 'tier0'];

    const price =
      period === SubscriptionType.Yearly ? yearlyPrice : monthlyPrice;
    const periodText =
      period === SubscriptionType.Yearly
        ? {
            title: 'Yearly',
            detail: 'year',
          }
        : {
            title: 'Monthly',
            detail: 'month',
          };

    let subtitle: string | undefined;
    if (price && currency) {
      subtitle =
        currency +
        numbro(price[currency] / 100).format({
          thousandSeparated: true,
          mantissa: 2,
        }) +
        ' / ' +
        t(periodText.detail);
    } else if (expiredAt) {
      const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      };
      const formattedDate = new Intl.DateTimeFormat(
        i18n.language,
        options,
      ).format(expiredAt);

      let prefix: string;
      if (expiredAt < new Date()) {
        prefix = 'Expired: ';
      } else if (willAutoRenew) {
        prefix = 'Next payment: ';
      } else if (overlayTag === SubscriptionCardOverlayTag.Scheduled) {
        prefix = 'To be activated: ';
      } else {
        prefix = 'Expire on: ';
      }

      subtitle = t(prefix) + formattedDate;
    }

    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={isTrial ? styles.trialHeader : styles.header}>
            <div className={styles.headerText}>
              <div className={styles.headerTitleRow}>
                <div className={styles.headerTitle}>
                  {period && !currency
                    ? `${t(name)} - ${t(periodText.title)}`
                    : t(name)}
                </div>
                {!tier && <div className={styles.headerFree}>{t('FREE')}</div>}
              </div>
              {subtitle && (
                <div className={styles.headerSubtitle}>{subtitle}</div>
              )}
            </div>
            {onClickSubscribe ? (
              <div className={styles.detailButton} onClick={onClickSubscribe}>
                <div>{t('Details')}</div>
                <SvgIcon
                  name={SVGIconType.Arrow}
                  width={14}
                  height={14}
                  color={'#00a698'}
                />
              </div>
            ) : (
              <div
                className={styles.headerExpand}
                onClick={() => setIsExpanded((e) => !e)}
              >
                <SvgIcon
                  name={SVGIconType.Arrow}
                  width={24}
                  height={24}
                  style={{ rotate: isExpanded ? '270deg' : '90deg' }}
                  color="black"
                />
              </div>
            )}
          </div>
          {!onClickSubscribe && (
            <div className={styles.body}>
              <div
                className={styles.bodyList}
                style={{ backgroundColor: isTrial ? '#e5e5e5' : '#fff' }}
              >
                <div className={styles.bodyRow}>
                  <SvgIcon
                    className={
                      isExpanded || createdItemCount !== undefined
                        ? styles.rowIcon
                        : undefined
                    }
                    name={SVGIconType.Storage}
                    color={'#52B1D7'}
                    width={24}
                    height={24}
                  />
                  <div className={styles.rowContent}>
                    <p className={styles.rowTitle}>
                      <Trans
                        i18nKey="Cloud Storage: {n} Items"
                        values={{ n: storage }}
                      >
                        Cloud Storage:
                        <span className={styles.greenText}>
                          <span className={styles.boldText}>150</span>
                          Items
                        </span>
                      </Trans>
                    </p>
                    {createdItemCount !== undefined && (
                      <>
                        <ProgressBar
                          current={createdItemCount}
                          total={storage}
                          height="8px"
                          backgroundColor="#f2f2f2"
                          fillColor={
                            createdItemCount <= storage ? '#45b8af' : '#ff5959'
                          }
                        />
                        <p
                          className={
                            createdItemCount <= storage
                              ? styles.storageUsage
                              : styles.outOfStorage
                          }
                        >
                          <Trans
                            i18nKey="Cloud Storage Usage"
                            values={{
                              n: createdItemCount,
                              total: storage,
                              percentage: `${Math.round(
                                (createdItemCount / storage) * 100,
                              )}%`,
                            }}
                          >
                            Usage:
                            <span className={styles.boldText}>250</span>
                            of 1000 items &#40;
                            <span className={styles.boldText}>25%</span>
                            &#41;
                          </Trans>
                        </p>
                      </>
                    )}
                    {isExpanded && (
                      <>
                        <p className={styles.rowText}>
                          {t(
                            'Including Memory Practice, Interactive Learning, Mini Challenge, Super Challenge, etc.',
                          )}
                        </p>
                        {createdItemCount !== undefined && (
                          <p className={styles.rowText}>
                            {t('To release more storage, remove some items.')}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <hr style={{ backgroundColor: '#ccc' }} />
                <div className={styles.bodyRow}>
                  <PngIcon
                    className={isExpanded ? styles.rowIcon : undefined}
                    name={'GameStamina'}
                    width={24}
                    height={24}
                  />
                  <div className={styles.rowContent}>
                    <p className={styles.rowTitle}>
                      <Trans
                        i18nKey="Practice Stamina: {n} Per Day"
                        values={{ n: token }}
                      >
                        Practice Stamina:
                        <span className={styles.greenText}>
                          <span className={styles.boldText}>10</span>
                          Per Day
                        </span>
                      </Trans>
                    </p>
                    {isExpanded && (
                      <p className={styles.rowText}>
                        {t('Refill Practice Stamina to {n} every day.', {
                          n: token,
                        })}
                      </p>
                    )}
                  </div>
                </div>
                <hr style={{ backgroundColor: '#ccc' }} />
                <div className={styles.bodyRow}>
                  <PngIcon
                    className={isExpanded ? styles.rowIcon : undefined}
                    name={'CreationStamina'}
                    width={24}
                    height={24}
                  />
                  <div className={styles.rowContent}>
                    <p className={styles.rowTitle}>
                      <Trans
                        i18nKey="Creation Stamina: {n} Per Day"
                        values={{ n: creationToken }}
                      >
                        Creation Stamina:
                        <span className={styles.greenText}>
                          <span className={styles.boldText}>10</span>
                          Per Day
                        </span>
                      </Trans>
                    </p>
                    {isExpanded && (
                      <p className={styles.rowText}>
                        {t('Refill Creation Stamina to {n} every day.', {
                          n: creationToken,
                        })}
                      </p>
                    )}
                  </div>
                </div>
                {unlockOcr && (
                  <>
                    <hr style={{ backgroundColor: '#ccc' }} />
                    <div className={styles.bodyRow}>
                      <SvgIcon
                        className={isExpanded ? styles.rowIcon : undefined}
                        name={SVGIconType.OCRScan}
                        color={'#ECAC54'}
                        width={24}
                        height={24}
                      />
                      <div className={styles.rowContent}>
                        <p className={styles.rowTitle}>
                          {t('Unlock: Scan Text from Image')}
                        </p>
                        {isExpanded && (
                          <p className={styles.rowText}>
                            {t(
                              'Unlock the OCR feature to effortlessly scan and extract text from images.',
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}
                {(onClickUpgrade || onClickDetail) && (
                  <>
                    <hr style={{ backgroundColor: '#ccc' }} />
                    <div>
                      {onClickUpgrade && (
                        <div
                          className={styles.upgradeButton}
                          onClick={onClickUpgrade}
                        >
                          <SvgIcon
                            name={SVGIconType.SubscriptionUpgrade}
                            width={24}
                            height={24}
                            color={'#ffffff'}
                          />
                          <div>{t('Upgrade')}</div>
                          <LightSwipe height={38} />
                        </div>
                      )}
                      {onClickDetail && (
                        <div
                          className={styles.detailButton}
                          onClick={onClickDetail}
                        >
                          <div>{t('Details')}</div>
                          <SvgIcon
                            name={SVGIconType.Arrow}
                            width={14}
                            height={14}
                            color={'#00a698'}
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              {onClickRegister && (
                <div className={styles.bodyList}>
                  <div className={styles.registerText}>
                    {t('Register to get FREE Basic Plan!')}
                  </div>
                  <div
                    className={styles.upgradeButton}
                    onClick={onClickRegister}
                  >
                    <SvgIcon
                      name={SVGIconType.Register}
                      width={24}
                      height={24}
                      color={'#ffffff'}
                    />
                    <div>{t('Register')}</div>
                    <LightSwipe />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {overlayTag && (
          <div className={styles.overlayTag}>
            <SvgIcon
              name={
                overlayTag === SubscriptionCardOverlayTag.Scheduled
                  ? SVGIconType.Period
                  : SVGIconType.ConfirmTick
              }
              color={'white'}
              height={14}
              width={14}
            />
            <p className={styles.overlayText}>{t(overlayTag)}</p>
          </div>
        )}
      </div>
    );
  },
);
